import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import LoaderComponent from '@components/LoaderComponent';
import { toastSuccess, toastWarning } from '@components/Toast';

import { fetchOrderPlaceWorkflow } from '@store/orders/actions';

import ApiClient from '@lib/apiClient';

import { getUserInfo, printApiError } from '@utils/Util';

const OrderDetailsTeamTab = ({ id }) => {
	const [state, setState] = useState({
		loading: false,
		memberList: {},
		clientName: '',
		managerName: '',
		memberName: '',
		clientId: '',
		managerId: '',
		memberId: '',
		tabLoader: false,
		suggestions: [],
		teamUserType: [
			{ value: 'BUYER', label: 'Buyer' },
			{ value: 'PROJECT_MANAGER', label: 'Project manager' },
			{ value: 'ACCOUNT_MANAGER', label: 'Account manager' },
			{ value: 'MERCHANDISER', label: 'Merchandiser' },
			{ value: 'QA', label: 'QA' }
		],
		addTeamPerson: '',
		allTypes: []
	});
	const teamRef = useRef(null);
	const resultsRef = useRef();
	const inputRef = useRef();
	const dispatch = useDispatch();

	const fetchProjectMemberList = () => {
		setState({ ...state, loading: true });
		ApiClient.GET('getProjectMemberList', id)
			.then(({ data }) => {
				dispatch(fetchOrderPlaceWorkflow(id));
				if (data) {
					let members = {};
					members['BUYER'] = data.client;
					members['ACCOUNT_MANAGER'] = data.accountManager;
					members['PROJECT_MANAGER'] = data.projectManager;

					members['MERCHANDISER'] = data.memberList
						.map((item) => item.primaryUserType === 'MERCHANDISER' && item)
						.filter((item) => item);

					members['QA'] = data.memberList
						.map((item) => item.primaryUserType === 'QA' && item)
						.filter((item) => item);

					members['OTHERS'] = data.memberList
						.map(
							(item) =>
								item.primaryUserType !== 'QA' &&
								item.primaryUserType !== 'MERCHANDISER' &&
								item
						)
						.filter((item) => item);

					setState({
						...state,
						loading: false,
						memberList: members,
						clientId: data.clientId ? data.clientId : '',
						clientName: data.clientName ? data.clientName : '',
						managerName: data.managerName ? data.managerName : '',
						managerId: data.managerId ? data.managerId : ''
					});
				} else {
					setState({ ...state, loading: false });
				}
			})
			.catch((error) => {
				setState({ ...state, loading: false });
				printApiError(error);
			});
	};

	useEffect(() => {
		document.addEventListener('mousedown', handleClickOutside);
		fetchProjectMemberList();
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	useEffect(() => {
		document.addEventListener('keydown', onKeyDown);

		return () => {
			document.removeEventListener('keydown', onKeyDown);
		};
	}, [state.allTypes]);

	const onKeyDown = (event) => {
		const isUp = event.key === 'ArrowUp';
		const isDown = event.key === 'ArrowDown';
		const inputIsFocused = document.activeElement === inputRef?.current;
		const resultsItems = Array.from(resultsRef?.current?.children || []);
		const activeResultIndex = resultsItems.findIndex((child) => {
			return child === document.activeElement;
		});

		if (isUp) {
			if (inputIsFocused) {
				resultsItems[resultsItems.length - 1].focus();
			} else if (resultsItems[activeResultIndex - 1]) {
				resultsItems[activeResultIndex - 1].focus();
			} else {
				inputRef && inputRef.current.focus();
			}
		}

		if (isDown) {
			if (inputIsFocused) {
				resultsItems[0].focus();
			} else if (resultsItems[activeResultIndex + 1]) {
				resultsItems[activeResultIndex + 1].focus();
			} else {
				inputRef && inputRef.current.focus();
			}
		}
	};

	const handleClickOutside = (event) => {
		if (teamRef && teamRef.current !== null && !teamRef.current.contains(event.target)) {
			setState((prev) => ({
				...prev,
				addTeamPerson: ''
			}));
		}
	};

	const getLabel = (key) => {
		if (key === 'OTHERS') return 'Others';
		return state.teamUserType.find((userType) => userType.value === key)['label'];
	};

	const allMembers = (item, isSearch) => {
		setState((prev) => ({
			...prev,
			tabLoader: true
		}));
		let params = {};
		if (item !== 'OTHERS') {
			params.userTypeList = item;
		}
		if (isSearch) {
			params.search = isSearch;
		}
		params.page = 0;
		params.size = 1000;
		ApiClient.GET('userSearch', params)
			.then(({ data }) => {
				setState((prev) => ({
					...prev,
					allTypes: data.data,
					tabLoader: false
				}));
			})
			.catch((error) => {
				setState((prev) => ({
					...prev,
					tabLoader: false
				}));
				printApiError(error);
			});
	};

	const setExecutive = (item, person) => {
		setState({ ...state, tabLoader: true });
		let url = item === 'ACCOUNT_MANAGER' ? 'addAccountManagerToOrder' : 'addProjectManagerToOrder';
		let urlSegment = `${person.id}/${id}`;

		ApiClient.POST(url, '', urlSegment)
			.then(({ data }) => {
				fetchProjectMemberList();
				toastSuccess(data.message);
				setState({ ...state, tabLoader: false });
			})
			.catch((error) => {
				setState({ ...state, tabLoader: false });
				printApiError(error);
			});
	};

	const assignPerson = (person) => {
		setState({ ...state, tabLoader: true });
		let bool = isExistMember(state.addTeamPerson, person.id);
		let url = bool === true ? 'removeMemberFromOrder' : 'addMemberToOrder';

		let memberBody = [person.id];

		ApiClient.POST(url, memberBody, id)
			.then(({ data }) => {
				fetchProjectMemberList();
				toastSuccess(data.message);
				setState({ ...state, tabLoader: false });
			})
			.catch((error) => {
				setState({ ...state, tabLoader: false });
				printApiError(error);
			});
	};

	const isExistMember = (item, id) => {
		let foundMember;
		if (Array.isArray(state.memberList[item])) {
			foundMember = state.memberList[item].find((person) => person.memberId === id);
			return foundMember ? Object.keys(foundMember).length > 0 : false;
		} else {
			foundMember = state.memberList[item]?.memberId === id;
			return foundMember;
		}
	};

	const userWiseTeamMember = (item) => {
		let { id, primaryUserType } = getUserInfo();
		let { memberList, addTeamPerson } = state;

		if (primaryUserType !== item) {
			if (item === 'BUYER') {
				toastWarning("Sorry, You can't assign/change buyer.");
			} else if (
				primaryUserType === 'ADMIN' ||
				((primaryUserType === 'ACCOUNT_MANAGER' || primaryUserType === 'PROJECT_MANAGER') &&
					(id === memberList['ACCOUNT_MANAGER'].memberId ||
						id === memberList['PROJECT_MANAGER'].memberId))
			) {
				if (addTeamPerson === '') {
					allMembers(item);
				}
				setState({
					...state,
					addTeamPerson: addTeamPerson ? '' : item
				});
			} else if (
				(item === 'ACCOUNT_MANAGER' || item === 'PROJECT_MANAGER') &&
				!(
					id === memberList['ACCOUNT_MANAGER'].memberId ||
					id === memberList['PROJECT_MANAGER'].memberId
				)
			) {
				let title = item === 'ACCOUNT_MANAGER' ? 'account manager' : 'project manager';
				let msg = `Sorry, you are not ${title} of this project`;
				toastWarning(msg);
			} else {
				toastWarning("Sorry, You can't assign/change team members.");
			}
		} else {
			toastWarning("Sorry, You can't replace yourself.");
		}
	};

	const assignedMembers = (item) => {
		let member = state.memberList[item];
		return (
			<div
				className={
					state.memberList[item]?.length > 1
						? 'single-team-person multiperson d-flex justify-content-between'
						: 'single-team-person d-flex justify-content-between'
				}
			>
				<div className="person-title d-flex justify-content-between">
					<p className="regular-14 gray_dark_01">{getLabel(item)}</p>
					<span className="position-relative">
						<img
							src={`${process.env.PUBLIC_URL}/icons/add-people.svg`}
							alt="add"
							className="cursor-pointer"
							onClick={() => {
								userWiseTeamMember(item);
								setTimeout(() => {
									inputRef.current && inputRef.current.focus();
								}, 0);
							}}
						/>
						{state.addTeamPerson === item && (
							<div className="assign-member shadow open" ref={teamRef}>
								<div className="title d-flex justify-content-between">
									<p className="semibold-16">Assign member</p>
									<img
										src={`${process.env.PUBLIC_URL}/icons/close.svg`}
										alt="close"
										className="cursor-pointer"
										onClick={() => {
											userWiseTeamMember(item);
										}}
									/>
								</div>
								<div className="member-list-container pt-0 scroll-y-label">
									<div className="search-filter bg-white">
										<div className="with-icon right-icon">
											<input
												type="text"
												autoComplete="off"
												placeholder="Search with email or name"
												onChange={(e) => allMembers(item, e.target.value)}
												ref={inputRef}
											/>
											<span className="right-icon">
												<img
													src={`${process.env.PUBLIC_URL}/icons/search.png`}
													alt="search"
												/>
											</span>
										</div>
									</div>
									<div ref={resultsRef}>
										<LoaderComponent loading={state.tabLoader}>
											{state.allTypes.length > 0 ? (
												state.allTypes.map((person, index) => (
													<div
														key={`person_${index}`}
														className={
															isExistMember(item, person.id)
																? 'member-list added'
																: 'member-list'
														}
														onClick={() => {
															if (
																item === 'ACCOUNT_MANAGER' ||
																item === 'PROJECT_MANAGER'
															) {
																setExecutive(item, person);
															} else {
																assignPerson(person);
															}
														}}
														tabIndex={0}
													>
														{person?.profilePicDocument?.docUrl ? (
															<img
																src={person?.profilePicDocument?.docUrl}
																alt="profile"
															/>
														) : (
															<img
																src={`${process.env.PUBLIC_URL}/images/pro_pic_default.svg`}
																alt="profile"
															/>
														)}
														<div className="name">{person.name}</div>
													</div>
												))
											) : (
												<div className="empty-state">
													<p className="regular-14 gray_dark_02 mt-3">
														No member found!
													</p>
												</div>
											)}
										</LoaderComponent>
									</div>
								</div>
							</div>
						)}
					</span>
				</div>
				<div className="person-details">
					{state.memberList[item] && Object.keys(state.memberList[item])?.length > 0 ? (
						<div className="person-info d-flex align-items-center">
							<div className="image">
								{member.memberImage ? (
									<img src={member?.memberImage} alt="person" />
								) : (
									<img
										src={`${process.env.PUBLIC_URL}/images/pro_pic_default.svg`}
										alt="profile"
									/>
								)}{' '}
							</div>
							<div className="details-info">
								<div className="name">
									<p>
										{member.memberName}
										{/*<span className="designation">{member.userType}</span>*/}
									</p>
									<p className="email">{member.email}</p>
								</div>
							</div>
						</div>
					) : (
						<div className="person-info d-flex align-items-center"></div>
					)}
				</div>
			</div>
		);
	};

	const reassignedMembers = (item) => {
		return (
			<div
				className={
					state.memberList[item].length > 1
						? 'single-team-person multiperson d-flex justify-content-between'
						: 'single-team-person d-flex justify-content-between'
				}
			>
				<div className="person-title d-flex justify-content-between">
					<p className="regular-14 gray_dark_01">{getLabel(item)}</p>
					<span className="position-relative">
						<img
							src={`${process.env.PUBLIC_URL}/icons/add-people.svg`}
							alt="add"
							className="cursor-pointer"
							onClick={() => userWiseTeamMember(item)}
						/>
						{state.addTeamPerson === item && (
							<div className="assign-member shadow open" ref={teamRef}>
								<div className="title d-flex justify-content-between">
									<p className="semibold-16">Assign member</p>
									<img
										src={`${process.env.PUBLIC_URL}/icons/close.svg`}
										alt="close"
										className="cursor-pointer"
										onClick={() => {
											userWiseTeamMember(item);
											setTimeout(() => {
												inputRef.current && inputRef.current.focus();
											}, 0);
										}}
									/>
								</div>
								<div className="member-list-container pt-0 scroll-y-label">
									<div className="search-filter bg-white">
										<div className="with-icon right-icon">
											<input
												type="text"
												autoComplete="off"
												placeholder="Search with email or name"
												onChange={(e) => allMembers(item, e.target.value)}
												ref={inputRef}
												// tabIndex={-1}
											/>
											<span className="right-icon">
												<img
													src={`${process.env.PUBLIC_URL}/icons/search.png`}
													alt="search"
												/>
											</span>
										</div>
									</div>
									<div ref={resultsRef}>
										<LoaderComponent loading={state.tabLoader}>
											{state.allTypes.length > 0 ? (
												state.allTypes.map((person, index) => (
													<div
														key={`member_${index}`}
														className={
															isExistMember(item, person.id)
																? 'member-list added'
																: 'member-list'
														}
														onClick={() => {
															if (
																item === 'ACCOUNT_MANAGER' ||
																item === 'PROJECT_MANAGER'
															) {
																setExecutive(item, person);
															} else {
																assignPerson(person);
															}
														}}
														// tabIndex={0}
													>
														{person?.profilePicDocument?.docUrl ? (
															<img
																src={person?.profilePicDocument?.docUrl}
																alt="profile"
															/>
														) : (
															<img
																src={`${process.env.PUBLIC_URL}/images/pro_pic_default.svg`}
																alt="profile"
															/>
														)}
														<div className="name">{person.name}</div>
													</div>
												))
											) : (
												<div className="empty-state">
													<p className="regular-14 gray_dark_02 mt-3">
														No member found!
													</p>
												</div>
											)}
										</LoaderComponent>
									</div>
								</div>
							</div>
						)}
					</span>
				</div>
				<div className="person-details">
					{state.memberList[item] && state.memberList[item].length > 0 ? (
						state.memberList[item].map((member, j) => {
							return (
								<div key={j} className="person-info d-flex align-items-center">
									<div className="image">
										{member.memberImage ? (
											<img src={member?.memberImage} alt="person" />
										) : (
											<img
												src={`${process.env.PUBLIC_URL}/images/pro_pic_default.svg`}
												alt="profile"
											/>
										)}{' '}
									</div>
									<div className="details-info">
										<div className="name">
											<p>
												{member.memberName}
												{/*<span className="designation">{member.userType}</span>*/}
											</p>
											<p className="email">{member.email}</p>
										</div>
									</div>
								</div>
							);
						})
					) : (
						<div className="person-info d-flex align-items-center"></div>
					)}
				</div>
			</div>
		);
	};

	const findUser = () => {
		let returnObj = [];
		for (let item in state.memberList) {
			if (item === 'ACCOUNT_MANAGER' || item === 'PROJECT_MANAGER' || item === 'BUYER') {
				returnObj.push(assignedMembers(item));
			} else {
				returnObj.push(reassignedMembers(item));
			}
		}
		return returnObj;
	};

	return (
		<LoaderComponent loading={state.loading}>
			<div className="create-order-container">
				<div className="team-conainer team-section">
					<div className="team-title mb-3">
						<h3 className="semibold-16 mb-0">Create team</h3>
					</div>
					<div className="team-body">{findUser().map((item) => item)}</div>
				</div>
			</div>
		</LoaderComponent>
	);
};

export default OrderDetailsTeamTab;
